<template>
  <div class="user-page">
    <PageHeader :title="giftTitle" icon="uil-gift" />

    <div v-if="history_errors != null" class="uk-alert-danger bg-soft-danger position_relative" uk-alert>
      <a class="uk-alert-close" uk-close></a>
      <p class="m-0" style="color: red">{{ history_errors }}</p>
    </div>

    <div class="gift-page-grid">
      <div class="gift-sidebar-section">
        <div class="gift-points-section">
          <GiftPointsCardSkeleton v-if="sidebarLoading"/>
          <GiftPointsCard v-else
                          :loading="sidebarLoading"
                          :user-coin="formattedUserCoin"
                          :user-point="user_point"
                          @convert-coin="convertToCoin"/>
        </div>

        <div class="gift-history-section">
          <GiftHistoryCardSkeleton v-if="sidebarLoading"/>
          <GiftHistoryCard v-else
                           :gift-histories="formattedGiftHistories"
                           :has-more="gift_history_has_more"
                           :loading="sidebarLoading"
                           :loading-more="gift_history_loading_more"
                           @load-more="loadMoreGiftHistory"/>
        </div>
      </div>

      <div class="gift-main-section">
        <div class="section-header pb-0 mb-4">
          <div class="section-header-left">
            <input v-model="filter.search" :placeholder="$t('gift.search')" type="text"/>
          </div>

          <div class="section-header-right">
            <multiple-select-input v-model="selectedSort"
                                   :allow-empty="false"
                                   :multiple="false"
                                   :searchable="false"
                                   :show-labels="false"
                                   :values="sortOptions"
                                   label="name"
                                   name="sortOrder"
                                   track-by="id"
            />
          </div>
        </div>

        <template v-if="giftsLoading">
          <GiftCardSkeleton v-for="n in 5" :key="'skeleton_' + n"/>
        </template>

        <template v-else>
          <div v-if="formattedGifts.length === 0" class="empty-gift-state">
            <div class="empty-gift-icon">
              <i class="uil-gift"></i>
            </div>
            <h4 class="empty-gift-title">{{ $t('general.no_result_found') || 'Veri BulunamadÄ±' }}</h4>
          </div>

          <template v-else>
            <div class="gift-list-card">
              <div class="gift-list-body">
                <GiftCard v-for="gift in formattedGifts"
                      :key="'gift_' + gift.id"
                      :gift="gift"
                      :user-coin="user_coin"
                      @get-gift="onGetGift"
                />
              </div>
              <div class="pagination-footer">
                <pagination v-if="pagination && pagination.hasOwnProperty('links')" :pagination="pagination"
                          @changePage="setPage($event)"/>
              </div>
            </div>        
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import axios from "axios";
import Pagination from "@/assets/components/Pagination.vue";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";
import GiftCard from "./components/GiftCard.vue";
import GiftCardSkeleton from "./components/GiftCardSkeleton.vue";
import GiftPointsCard from "./components/GiftPointsCard.vue";
import GiftHistoryCard from "./components/GiftHistoryCard.vue";
import GiftPointsCardSkeleton from "./components/GiftPointsCardSkeleton.vue";
import GiftHistoryCardSkeleton from "./components/GiftHistoryCardSkeleton.vue";
import PageHeader from "@/view/components/common/PageHeader.vue";
import moment from "moment";
import { getEnvironmentMediaUrl } from "@/utils/imageHelper";
import { MOMENT_DATE_FORMAT_FOR_KT } from "@/core/config/constant";
import widgetMixin from "@/core/mixin/widget.mixin";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: 'aes', isCompression: true });

export default {
  name: "Gifts",
  mixins: [widgetMixin],
  components: {
    Pagination,
    MultipleSelectInput,
    GiftCard,
    GiftCardSkeleton,
    GiftPointsCard,
    GiftHistoryCard,
    GiftPointsCardSkeleton,
    GiftHistoryCardSkeleton,
    PageHeader
  },
  data() {
    return {
      status_id: 1,
      pagination: null,
      page: 1,
      per_page: 5,
      filter: {
        search: null,
        orderBy: "created_at",
        sort: "desc",
      },
      selectedSort: null,
      menuList: [],
      giftsLoading: true,
      sidebarLoading: true,
      gifts: null,
      user_point: 0,
      user_coin: 0,
      gift_history: null,
      gift_history_page: 1,
      gift_history_per_page: 10,
      gift_history_loading_more: false,
      gift_history_has_more: true,
      errors: null,
      history_errors: null
    };
  },
  computed: {
    sortOptions() {
      return [
        { id: 'created_at_desc', name: this.$t("filters.order.latest_added"), orderBy: 'created_at', sort: 'desc' },
        { id: 'score_asc', name: this.$t("gift.filter.highest_point"), orderBy: 'score', sort: 'asc' },
        { id: 'score_desc', name: this.$t("gift.filter.lowest_point"), orderBy: 'score', sort: 'desc' }
      ];
    },
    giftTitle() {
      const sourceMenu = this.getDashboardMenuItems(this.menuList);
      const menuItem = sourceMenu.find(item => item.type === 'gift');
      return this.getLocalizedWidgetTitle(menuItem, this.$t('gift.shop'), this.$i18n?.locale);
    },
    formattedUserCoin() {
      if (this.user_coin === "NaN" || this.user_coin === null || this.user_coin === undefined) {
        return 0;
      }
      return Number(this.user_coin).toFixed(2);
    },
    formattedGifts() {
      let results = [];
      if (this.gifts === null || !("data" in this.gifts)) return [];
      this.pagination = this.gifts;
      this.gifts.data.forEach((currentItem) => {
        results.push({
          id: currentItem.id,
          name: currentItem.name,
          description: currentItem.description ? currentItem.description : "-",
          score: currentItem.score,
          stock_quantity: currentItem.stock_quantity - currentItem.used_quantity,
          used_quantity: currentItem.used_quantity,
          address_required: currentItem.address_required,
          photo_link: getEnvironmentMediaUrl(currentItem.image_link),
        });
      });

      return results;
    },
    formattedGiftHistories() {
      let results = [];
      if (!this.gift_history || !this.gift_history.data || !Array.isArray(this.gift_history.data))
        return [];
      this.gift_history.data.forEach((currentItem) => {
        if (currentItem && currentItem.gift) {
          results.push({
            image: currentItem.gift.image_link ? getEnvironmentMediaUrl(currentItem.gift.image_link) : "-",
            name: currentItem.name || currentItem.gift.name || "-",
            point: currentItem.point || 0,
            date: currentItem.created_at ? moment(currentItem.created_at).format(MOMENT_DATE_FORMAT_FOR_KT) : "-",
          });
        }
      });

      return results;
    },
  },
  methods: {
    async getGiftResult() {
      this.giftsLoading = true;
      this.errors = null;
      try {
        const response = await axios.get(process.env.VUE_APP_BACKEND_APP_URL + '/api/gift', {
          params: {
            like: this.filter.search,
            page: this.page,
            per_page: this.per_page,
            sort: this.filter.sort,
            order_by: this.filter.orderBy,
            status_id: this.status_id,
          },
        });
        this.gifts = response.data;
        this.pagination = response.data;
      } catch (error) {
        this.errors = error;
      } finally {
        this.giftsLoading = false;
      }
    },
    async convertToCoin() {
      try {
        await axios.post(process.env.VUE_APP_BACKEND_APP_URL + '/api/point/convert', {});
        await this.getUserPoint();
        await this.getUserCoin();
      } catch (error) {
        this.errors = error;
      }
    },
    async getUserPoint() {
      try {
        const response = await axios.get(process.env.VUE_APP_BACKEND_APP_URL + '/api/point/point');
        this.user_point = response.data || 0;
      } catch (error) {
        this.errors = error;
      }
    },
    async getUserCoin() {
      try {
        const response = await axios.get(process.env.VUE_APP_BACKEND_APP_URL + '/api/point/coin');
        this.user_coin = response.data || 0;
      } catch (error) {
        this.errors = error;
      }
    },
    async getGiftHistoryResult(reset = true) {
      if (reset) {
        this.gift_history_page = 1;
        this.gift_history_has_more = true;
        this.gift_history = { data: [] };
        this.sidebarLoading = true;
      }
      
      if (!this.gift_history_has_more || this.gift_history_loading_more) return;

      this.gift_history_loading_more = true;
      this.history_errors = null;
      try {
        const response = await axios.get(process.env.VUE_APP_BACKEND_APP_URL + '/api/gift-history', {
          params: {
            page: this.gift_history_page,
            per_page: this.gift_history_per_page,
            orderBy: "created_at",
            sort: "desc",
            user_gifts: true,
            status_id: 2,
          },
        });

        let newData = [];
        if (Array.isArray(response.data)) {
          newData = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          newData = response.data.data;
          if (response.data.last_page && this.gift_history_page >= response.data.last_page) {
            this.gift_history_has_more = false;
          }
        } else {
          this.gift_history_has_more = false;
        }

        if (newData.length === 0) {
          this.gift_history_has_more = false;
        } else {
          if (reset) {
            this.gift_history = { data: newData };
          } else {
            this.gift_history.data = [...(this.gift_history.data || []), ...newData];
          }
          this.gift_history_page++;
        }
      } catch (error) {
        this.history_errors = error;
        this.gift_history_has_more = false;
      } finally {
        this.gift_history_loading_more = false;
        if (reset) {
          this.sidebarLoading = false;
        }
      }
    },
    loadMoreGiftHistory() {
      if (!this.gift_history_loading_more && this.gift_history_has_more) {
        this.getGiftHistoryResult(false);
      }
    },
    onGetGift({ id, addressRequired }) {
      this.submitGift(id, addressRequired);
    },
    submitGift(id, address_required) {
      let input = address_required ? "textarea" : "";
      Swal.fire({
        title: (address_required) ? this.$t("gift.where_to") : this.$t("gift.sure_receive_gift"),
        input: input,
        inputAttributes: {
          autocapitalize: "off",
        },
        showCancelButton: true,
        confirmButtonText: this.$t('general.ok'),
        confirmButtonColor: "#ae71ff",
        cancelButtonText: this.$t('general.give_up'),
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
      }).then(async (result) => {
        if (result.isConfirmed) {
          this.history_errors = null;
          try {
            await axios.post(process.env.VUE_APP_BACKEND_APP_URL + '/api/gift-history', {
              gift_id: id,
              is_addres_required: address_required,
              address: result.value || ""
            });
            Swal.fire({
              confirmButtonText: this.$t('general.ok'),
              confirmButtonColor: "#ae71ff",
              title: this.$t('general.congratulation'),
              html: this.$t('gift.request_created'),
            });
            await this.getAllResults();
          } catch (error) {
            this.history_errors = error;
          }
        }
      });
    },
    setPage(page) {
      this.page = page;
      this.getGiftResult();
    },
    async getAllResults() {
      await Promise.all([
        this.getUserPoint(),
        this.getUserCoin(),
        this.getGiftHistoryResult(),
        this.getGiftResult()
      ]);
    },
  },
  async mounted() {
    try {
      await this.getWidgetsMixin();

      if (!this.isGiftActiveMixin) {
        await this.$router.push({ name: 'user.index' });
        return;
      }

      this.menuList = this.menuListMixin || [];
      this.selectedSort = this.sortOptions[0];
      await this.getAllResults();
    } catch (error) {
      await this.$router.push({ name: 'user.index' });
    }
  },
  watch: {
    "filter.search": function () {
      this.debouncedFilterSearch();
    },
    selectedSort(val) {
      if (val) {
        this.page = 1;
        this.filter.orderBy = val.orderBy;
        this.filter.sort = val.sort;
        this.getGiftResult();
      }
    },
  },
  created: function () {
    this.debouncedFilterSearch = _.debounce(this.getGiftResult, 500);
  },
};
</script>

<style scoped>
.section-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: nowrap;
}

.section-header-left input {
  height: 36px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
  box-sizing: border-box;
}

.section-header-left input:focus {
  outline: none;
  border-color: var(--backgroundColor, #6366f1);
}

.section-header-right {
  min-width: 180px;
}

.section-header-right :deep(.form-group) {
  margin-bottom: 0;
}

.section-header-right :deep(.multiselect) {
  min-width: 180px;
  font-size: 14px;
}

.section-header-right :deep(.multiselect__tags) {
  min-height: 36px;
  padding: 6px 40px 0 8px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.section-header-right :deep(.multiselect__single) {
  font-size: 13px;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.section-header-right :deep(.multiselect__option) {
  font-size: 13px;
  padding: 10px 12px;
  white-space: nowrap;
}

.section-header-right :deep(.multiselect__option--highlight) {
  background: var(--backgroundColor, #6366f1);
}

.gift-page-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.gift-sidebar-section,
.gift-points-section,
.gift-main-section,
.gift-history-section {
  min-width: 0;
}

.gift-sidebar-section {
  display: contents;
}

.gift-points-section {
  order: 1;
}

.gift-main-section {
  order: 2;
}

.gift-history-section {
  order: 3;
}

.gift-list-card {
  background: #fff;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  box-shadow: none !important;
  overflow: hidden;
}

.gift-list-body {
  padding: 16px 16px 0;
}

.gift-list-body :deep(.gift-card:last-child) {
  margin-bottom: 0;
}

@media (min-width: 960px) {
  .gift-page-grid {
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(280px, 2fr);
    align-items: start;
  }

  .gift-main-section {
    grid-column: 1;
    grid-row: 1;
    order: initial;
  }

  .gift-sidebar-section {
    grid-column: 2;
    grid-row: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

@media (max-width: 576px) {
  .section-header {
    flex-wrap: nowrap;
    gap: 8px;
  }

  .section-header-left {
    flex: 1;
    min-width: 0;
  }

  .section-header-left input {
    width: 100%;
    height: 32px;
    font-size: 12px;
    padding: 4px 10px;
  }

  .section-header-right {
    min-width: 140px;
    flex-shrink: 0;
  }

  .section-header-right :deep(.multiselect) {
    min-width: 140px;
    font-size: 12px;
  }

  .section-header-right :deep(.multiselect__tags) {
    min-height: 32px;
    padding: 4px 36px 0 8px;
  }

  .section-header-right :deep(.multiselect__single) {
    font-size: 12px;
  }

  .section-header-right :deep(.multiselect__option) {
    font-size: 12px;
    padding: 8px 10px;
  }
}

/* Empty State */
.empty-gift-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  margin: 40px 0;
}

.empty-gift-icon {
  border-radius: 50%;
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
}

.empty-gift-icon i {
  font-size: 48px;
  color: #ffffff;
}

.empty-gift-title {
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
}

.pagination-footer {
  flex-shrink: 0;
  padding: 12px 16px 16px;
  border-top: 1px solid #e5e7eb;
  margin-top: 12px;
}


@media (max-width: 768px) {
  .empty-gift-state {
    padding: 40px 16px;
    margin: 24px 0;
  }

  .empty-gift-icon {
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
  }

  .empty-gift-icon i {
    font-size: 36px;
  }

  .empty-gift-title {
    font-size: 20px;
  }
}

@media (max-width: 576px) {
  .gift-list-card {
    border-radius: 10px;
  }

  .gift-list-body {
    padding: 10px 10px 0;
  }

  .pagination-footer {
    padding: 10px 10px 12px;
    margin-top: 10px;
  }

  .empty-gift-state {
    padding: 32px 12px;
    margin: 16px 0;
  }

  .empty-gift-icon {
    width: 70px;
    height: 70px;
    margin-bottom: 16px;
  }

  .empty-gift-icon i {
    font-size: 32px;
  }

  .empty-gift-title {
    font-size: 18px;
  }
}

/* Enterprise mobile/WebView sizing */
@media (max-width: 959px) {
  .user-page {
    --gift-touch-target: 48px;
    --gift-page-gap: 16px;
  }

  .user-page .section-header {
    align-items: stretch;
    flex-direction: row;
    gap: var(--gift-page-gap);
    margin-bottom: 16px;
  }

  .user-page .section-header-left {
    flex: 1 1 auto;
    min-width: 0;
    width: auto;
    display: flex;
    align-items: stretch;
  }

  .user-page .section-header-left input {
    width: 100%;
    box-sizing: border-box;
    margin: 0;
  }

  .user-page .section-header-right {
    flex: 0 0 clamp(144px, 34vw, 220px);
    width: clamp(144px, 34vw, 220px);
    min-width: 0;
    display: flex;
    align-items: stretch;
  }

  .user-page .section-header-right :deep(.multiselect) {
    width: 100%;
    min-width: 0;
    height: var(--gift-touch-target);
    box-sizing: border-box;
  }

  .user-page .section-header-right :deep(.form-group) {
    width: 100%;
    height: var(--gift-touch-target);
    margin: 0;
  }

  .user-page .section-header-left input {
    height: var(--gift-touch-target);
    min-height: var(--gift-touch-target);
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.5;
    border-radius: 10px;
  }

  .user-page .section-header-left input::placeholder,
  .user-page :deep(.multiselect__input)::placeholder {
    font-size: 16px;
  }

  .user-page .section-header-right :deep(.multiselect) {
    font-size: 16px;
  }

  .user-page .section-header-right :deep(.multiselect__tags) {
    min-height: var(--gift-touch-target);
    height: var(--gift-touch-target);
    box-sizing: border-box;
    display: flex;
    align-items: center;
    padding: 12px 40px 12px 16px;
    border-radius: 10px;
    overflow: hidden;
  }

  .user-page .section-header-right :deep(.multiselect__single) {
    display: block;
    min-width: 0;
    max-width: 100%;
    margin: 0;
    font-size: 16px;
    line-height: 1.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .user-page .section-header-right :deep(.multiselect__option) {
    min-height: 48px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.5;
    white-space: normal;
  }

  .user-page :deep(.gift-card) {
    min-width: 0;
  }

  .user-page :deep(.gift-btn),
  .user-page :deep(.convert-btn) {
    min-height: var(--gift-touch-target);
    box-sizing: border-box;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.25;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .user-page :deep(.convert-btn) {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 576px) {
  .user-page .section-header {
    gap: 8px;
  }

  .user-page .section-header-right {
    flex: 0 0 144px;
    width: 144px;
    min-width: 144px;
  }

  .user-page :deep(.gift-card) {
    align-items: stretch;
    margin-bottom: 12px;
  }

  .user-page :deep(.gift-card-image) {
    width: 88px;
    min-width: 88px;
  }

  .user-page :deep(.gift-card-content) {
    min-width: 0;
    padding: 12px;
  }

  .user-page :deep(.gift-header) {
    gap: 8px;
    margin-bottom: 8px;
  }

  .user-page :deep(.gift-name) {
    min-width: 0;
    font-size: 16px;
    line-height: 1.4;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  .user-page :deep(.mobile-badge) {
    flex-shrink: 0;
    padding: 4px 8px;
    font-size: 12px;
  }

  .user-page :deep(.gift-description) {
    display: -webkit-box;
    margin-bottom: 12px;
    font-size: 14px;
    line-height: 1.5;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }

  .user-page :deep(.gift-footer) {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
    margin-top: 0;
  }

  .user-page :deep(.gift-btn) {
    width: 100%;
    justify-content: center;
  }

  .user-page :deep(.gift-info) {
    margin-top: 0;
    text-align: left;
  }

  .user-page :deep(.coin-needed),
  .user-page :deep(.out-of-stock),
  .user-page :deep(.stock-info) {
    font-size: 12px;
    line-height: 1.5;
  }

  .user-page :deep(.points-card) {
    padding: 16px;
    margin-top: 0;
    border-radius: 12px;
  }

  .user-page :deep(.uil-coins.points-icon) {
    display: none !important;
  }

  .user-page :deep(.points-number) {
    font-size: 18px;
    line-height: 1.25;
  }

  .user-page :deep(.points-label) {
    font-size: 14px;
    line-height: 1.5;
  }

  .user-page :deep(.history-card) {
    min-height: 0;
    max-height: 420px;
    padding: 16px;
  }

  .user-page :deep(.history-header) {
    font-size: 16px;
    line-height: 1.5;
  }

  .user-page :deep(.history-item) {
    min-height: 56px;
    box-sizing: border-box;
    padding: 12px;
    gap: 8px;
  }

  .user-page :deep(.history-image) {
    width: 40px;
    height: 40px;
  }

  .user-page :deep(.history-name) {
    font-size: 14px;
    line-height: 1.5;
  }

  .user-page :deep(.history-point),
  .user-page :deep(.history-date) {
    font-size: 12px;
    line-height: 1.5;
  }
}
</style>
